import {
  Component,
  ChangeDetectionStrategy,
  DestroyRef,
  inject,
  input,
  output,
  model,
  computed,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslocoModule } from '@jsverse/transloco';
import { map } from 'rxjs/operators';
import { ContextDocument } from '../../models';
import { OrgManagementApi } from '../../services/org-management-api/org-management-api';
import { StateStoreService } from '../../services/state-store/state-store';
import { createContextDocPreviewHandler } from '../../shared/context-doc-preview-handler';
import { ContextDocPreviewComponent } from '../context-doc-preview/context-doc-preview';
import { DetailPanelComponent } from '../detail-panel/detail-panel';
import { EditablePanelTitleComponent } from '../editable-panel-title/editable-panel-title';
import { TypeIconComponent } from '../type-icon/type-icon';
import { UserAvatarComponent } from '../user-avatar/user-avatar';
import {
  formatFileSize,
  getContextDocFileTypeLabel,
  getContextDocIconConfig,
} from '../../utils/document.utils';
import { formatDate } from '../../utils/date.utils';
import { getDisplayAuthor } from '../../utils/display.utils';
import { getMemberDisplayName, stripUserIdPrefix } from '../../utils/user.utils';

@Component({
  selector: 'app-context-document-detail-panel',
  templateUrl: './context-document-detail-panel.html',
  styleUrl: './context-document-detail-panel.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatIconModule,
    MatMenuModule,
    MatProgressSpinnerModule,
    TranslocoModule,
    ContextDocPreviewComponent,
    DetailPanelComponent,
    EditablePanelTitleComponent,
    TypeIconComponent,
    UserAvatarComponent,
  ],
})
export class ContextDocumentDetailPanel {
  private readonly destroyRef = inject(DestroyRef);
  private readonly api = inject(OrgManagementApi);
  private readonly stateService = inject(StateStoreService);

  readonly contextDoc = input.required<ContextDocument>();
  readonly canManage = input<boolean>(false);
  /** True while the parent route is downloading this doc — drives the
   *  drawer's download button into a disabled + spinner state so the
   *  user can't double-fire and the in-flight slot is visible. */
  readonly isExporting = input<boolean>(false);
  readonly closed = output<void>();
  readonly downloadDocument = output<ContextDocument>();
  readonly delete = output<ContextDocument>();
  readonly replace = output<ContextDocument>();
  readonly width = model(500);

  readonly iconConfig = computed(() => getContextDocIconConfig(this.contextDoc().file_type));
  readonly fileTypeLabel = computed(() => getContextDocFileTypeLabel(this.contextDoc().file_type));
  /** The uploader as a member of the org on screen, when the viewer can see
   *  them. That record is the only place a stored name and profile picture
   *  live — the document itself carries whatever string the BE stamped at
   *  upload time, often the address. An uploader from another org (a manager
   *  filing into a customer's workspace) simply won't match. */
  private readonly uploaderMember = computed(() => {
    const id = this.contextDoc().uploaded_by;
    if (!id) return null;
    const rawId = stripUserIdPrefix(id);
    return this.stateService.orgMembers().find(m => stripUserIdPrefix(m.user_id) === rawId) ?? null;
  });

  readonly uploaderName = computed(() => {
    const doc = this.contextDoc();
    const member = this.uploaderMember();
    return (
      (member && getMemberDisplayName(member)) ||
      doc.uploaded_by_name ||
      getDisplayAuthor(doc.uploaded_by) ||
      null
    );
  });

  readonly uploaderPictureUrl = computed(() => this.uploaderMember()?.profile_picture_url ?? null);
  readonly canDownload = computed(() => !!this.contextDoc().download_url);

  /** Shared preview-modal handler. `refetch` asks the BE for a fresh
   *  presigned URL for *this* doc only (`document_ids: [id]`) — same
   *  contract the document/assessment drawers use. The handler closes
   *  the modal if the doc has been deleted server-side. */
  readonly contextDocPreview = createContextDocPreviewHandler({
    destroyRef: this.destroyRef,
    refetch: previewDoc => {
      const doc = previewDoc ?? this.contextDoc();
      const orgId = doc.org_id;
      if (!orgId) return null;
      const ownerQuery =
        doc.owner_id &&
        (doc.usage_scope === 'assessment' ||
          doc.usage_scope === 'document' ||
          doc.usage_scope === 'import' ||
          doc.usage_scope === 'session' ||
          doc.usage_scope === 'country_research')
          ? { source_owner_kind: doc.usage_scope, source_owner_id: doc.owner_id }
          : {};
      return this.api
        .getContextDocuments(orgId, {
          document_ids: [doc.context_document_id],
          ...ownerQuery,
        })
        .pipe(map(resp => resp.context_documents));
    },
  });

  readonly formatDate = formatDate;
  /** Canonical file-size formatter — `document.utils.ts`. The previous
   *  local copy threw `i.toFixed is not a function` when the BE sent
   *  `file_size` as a string (truthy non-number that skipped the
   *  loop). The shared util coerces via JS arithmetic so string inputs
   *  resolve cleanly, and it's the same primitive every other doc
   *  surface uses — no per-component reinvention. */
  readonly formatFileSize = formatFileSize;

  onClose(): void {
    this.closed.emit();
  }

  onDownload(): void {
    const doc = this.contextDoc();
    if (!doc.download_url || this.isExporting()) return;
    // Route owns the download orchestration (so the in-flight state can
    // also drive the row spinner on the list/grid). The actual blob
    // fetch + `<a download>` click lives in the route's
    // `onDownloadContextDocument` handler.
    this.downloadDocument.emit(doc);
  }

  onReplace(): void {
    this.replace.emit(this.contextDoc());
  }

  onDelete(): void {
    this.delete.emit(this.contextDoc());
  }
}
